import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { APIurl } from "../APIClient";
import { useAuthenticate, useGetOrganizationId } from "../cacheQueries";
import {
  refreshDetectionRemarks,
  upsertDetectionRemark,
} from "./detectionRemarksStore";
import { normalizeDetectionId } from "./detectionRemarksKeys";

type RemarksUpdatedPayload = {
  detectionId: number;
  remarks: string | null;
  encrypted?: boolean;
  updatedAt: string;
};

function remarksSseUrl(lastEventId: number | null): string {
  const base = `${APIurl}events/remarks`;
  return lastEventId == null ? base : `${base}?lastEventId=${lastEventId}`;
}

export default function RemarksSseListener() {
  const { data: user, isLoading: userLoading } = useAuthenticate();
  const { data: organizationId } = useGetOrganizationId();
  const queryClient = useQueryClient();

  // Resume point, kept across reconnects of the same org. The server only
  // replays buffered events with a higher id, so we don't re-apply old
  // updates over newer local saves. The browser's own auto-reconnect sends
  // it as Last-Event-ID; we pass it explicitly when we open a new
  // EventSource (org switch, re-login, remount).
  //
  // The server also sends a `remarks_resume` frame on every connect so we
  // hold an id even in a quiet org. When our id is no longer covered by its
  // buffer (events aged out, or the api restarted), it sends
  // `remarks_resync` and we re-read the remarks we cache. Nothing else
  // triggers a re-read, so a normal reconnect costs no extra requests.
  const lastEventIdRef = useRef<{ org: unknown; id: number } | null>(null);

  useEffect(() => {
    if (userLoading || !user || organizationId == null) return;

    if (lastEventIdRef.current?.org !== organizationId) {
      lastEventIdRef.current = null;
    }

    const es = new EventSource(
      remarksSseUrl(lastEventIdRef.current?.id ?? null),
      { withCredentials: true },
    );

    const recordEventId = (event: MessageEvent) => {
      const eventId = Number(event.lastEventId);
      if (Number.isSafeInteger(eventId)) {
        lastEventIdRef.current = { org: organizationId, id: eventId };
      }
    };

    const onResync = () => refreshDetectionRemarks(queryClient, organizationId);

    const onRemarksUpdated = (event: MessageEvent) => {
      recordEventId(event);

      let payload: RemarksUpdatedPayload;
      try {
        payload = JSON.parse(event.data) as RemarksUpdatedPayload;
      } catch {
        return;
      }

      const id = normalizeDetectionId(payload?.detectionId);
      if (id == null) return;
      const remarks = typeof payload.remarks === "string" ? payload.remarks : null;

      // Applying the same update twice is harmless (idempotent).
      upsertDetectionRemark(
        queryClient,
        organizationId,
        { id, encrypted: payload.encrypted === true },
        remarks,
      );
    };

    es.addEventListener("remarks_updated", onRemarksUpdated as EventListener);
    es.addEventListener("remarks_resume", recordEventId as EventListener);
    es.addEventListener("remarks_resync", onResync);

    return () => {
      es.removeEventListener("remarks_updated", onRemarksUpdated as EventListener);
      es.removeEventListener("remarks_resume", recordEventId as EventListener);
      es.removeEventListener("remarks_resync", onResync);
      es.close();
    };
  }, [organizationId, queryClient, user, userLoading]);

  return null;
}
